import { useMemo } from 'react';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import { tables as launcherTables } from '@sdk/launcher';
import {
  getStatusMessage,
  isDownloadActive,
  latestAppDownload,
  resolveHelperState,
  resolveInstallState,
  resolveLaunchVersion,
  resolveOrgApp,
  type AppDownload,
  type HelperState,
  type InstallState,
} from '../services/launcher/launchService';
import { useLauncherProbe } from '../services/launcher/launcherProbeStore';
import { useStoredLaunchAppId } from '../services/launcher/launchAppChoice';
import { useAuthKit } from '../contexts/AuthProvider';

const MINEZERO_APP_ID = import.meta.env.VITE_MINEZERO_APP_ID ?? 'minezero';

export type LauncherChannel = 'Stable' | 'Testing' | 'Dev';
/** Picker order, mirrors the TurboTwin webclient. */
export const LAUNCHER_CHANNELS: readonly LauncherChannel[] = ['Stable', 'Testing', 'Dev'];

/**
 * Shared "is the local launcher EXE ready to handle a launch?" hook.
 *
 * Extracted from the discovery logic at the top of
 * `src/components/LauncherButton.tsx` so any surface that wants to
 * dispatch a launch (the topbar widget AND the per-row Launch button on
 * TraineePortal cards added by MINETWIN-ORIENTATION-LAUNCH-PLAN Phase 1)
 * can share one source of truth for the readiness state
 * (`not-connected` / `connected-offline` / `connected-online` /
 * `running-other-user`), the
 * MineZero app row resolution (env id with substring fallback), the
 * channel-scoped most-recent `app_version`, and the `launchEnabled`
 * gate.
 *
 * `channel` defaults to `'Stable'` for per-row callers that don't expose
 * a picker; LauncherButton passes its own channel-selector state in.
 *
 * Return shape exposes the launch-ready primitives consumers need
 * (`effectiveAppId`, `exePath`, `versionId`) without leaking the raw
 * launcher row types — keeps the hook stable across launcher binding
 * regenerations.
 *
 * Follow-up (deferred): LauncherButton still inlines this discovery; a
 * separate cleanup can migrate it to consume this hook for a single
 * source of truth. Both impls were copied from the same source so they
 * cannot drift today.
 */
/**
 * Live download status for the resolved version, derived from the launcher's
 * `my_download_progress` table. Present while the launcher is downloading a
 * version (or has just finished/failed one) for the resolved `versionId`.
 */
export type LauncherDownload = {
  status: 'NotStarted' | 'InProgress' | 'Paused' | 'Completed' | 'Failed';
  /** 0-100, or `null` when total size isn't known yet. */
  percent: number | null;
  bytesDownloaded: bigint;
  totalBytes: bigint;
  /** Set when `status === 'Failed'`. */
  errorMessage: string | null;
  /** Free-text step message from the launcher (e.g. "Downloading 240 chunks"). */
  statusMessage: string | null;
};

export function useLauncherReadiness(channel: LauncherChannel = 'Stable'): {
  state: HelperState;
  statusMessage: string;
  /** Resolved MineZero app id — may differ from the env var when the
   *  fuzzy fallback (substring on appId/displayName) fires. */
  effectiveAppId: string;
  /** EXE path from the resolved `app` row, or `null` when the row is
   *  missing (rare — usually means `VITE_MINEZERO_APP_ID` is stale). */
  exePath: string | null;
  /** Resolved version_id to launch for the channel (pinned version first, then
   *  latest published on the channel); `undefined` when nothing resolves and
   *  the launcher should fall back to the installed default. */
  versionId: bigint | undefined;
  /** Human-readable version label for the resolved version (for tooltips);
   *  `undefined` when no version resolved. */
  versionLabel: string | undefined;
  /** True when the launcher EXE is online AND the app row resolved. */
  launchEnabled: boolean;
  /** Live download progress for the resolved version, or `null` when the
   *  launcher isn't (and hasn't recently been) downloading it. Drives the
   *  "Downloading… / Failed" feedback after a 202 `downloading` launch. */
  download: LauncherDownload | null;
  /** #193: whether the resolved version is on this machine, so the button
   *  can read Download / Update / Launch BEFORE the click. */
  installState: InstallState;
  /** #194: the newest download for this app whatever version — the global
   *  indicator must survive a channel switch. Null when there is none. */
  appDownload: AppDownload | null;
  /** #194: convenience — `appDownload` is InProgress or Paused. */
  downloadActive: boolean;
  /** Version label of the build `appDownload` is fetching, whatever channel
   *  this hook was asked about (the global banner must not show the selected
   *  channel's version for a download of another one). */
  appDownloadVersionLabel: string | undefined;
} {
  const launcherConn = useSpacetimeDB('launcher');
  // `my_org_*` views, not the raw `app` / `app_version` / `channel_version`
  // tables — the raw tables are private on the deployed turbotwin-launcher
  // and were failing the subscription batch. See LauncherButton.tsx for
  // the full rationale + TurboTwin webclient cross-reference.
  const [apps] = useTable('launcher', launcherTables.my_org_apps);
  const [appVersions] = useTable('launcher', launcherTables.my_org_app_versions);
  const [channelVersions] = useTable('launcher', launcherTables.my_org_channel_versions);
  const [connectedClients] = useTable('launcher', launcherTables.my_connected_clients);
  const [downloadProgress] = useTable('launcher', launcherTables.my_download_progress);
  const [installedVersions] = useTable('launcher', launcherTables.my_installed_versions);

  const { organizationId } = useAuthKit();
  // The admin's remembered per-org pick wins here too (#183) — this hook is
  // the THIRD launch surface and was still resolving without it, so a Join
  // from a trainee card could disagree with the topbar button.
  const rememberedAppId = useStoredLaunchAppId(organizationId);
  const mineZeroApp = useMemo(
    () => resolveOrgApp(apps, MINEZERO_APP_ID, rememberedAppId),
    [apps, rememberedAppId],
  );

  const effectiveAppId = mineZeroApp?.appId ?? MINEZERO_APP_ID;

  // Follow the per-channel pinned version (channel_version) first; that pin can
  // point at a build published under a different channel (e.g. Stable -> a Dev
  // build), which a published-channel filter alone would miss.
  const selectedVersion = useMemo(
    () => resolveLaunchVersion(appVersions, channelVersions, effectiveAppId, channel),
    [appVersions, channelVersions, channel, effectiveAppId],
  );

  const isLauncherExeRunning = useMemo(
    () => connectedClients.some((c) => c.clientType?.tag === 'App'),
    [connectedClients],
  );

  // Local-API probe tie-breaker (fed by useLauncherProbeDriver): upgrades a
  // stale-cache yellow to green (same user) or to `running-other-user` when
  // the single launcher instance is signed in as a different account.
  const probe = useLauncherProbe();
  const { user } = useAuthKit();


  const state: HelperState = resolveHelperState({
    connActive: launcherConn.isActive,
    stdbSaysRunning: isLauncherExeRunning,
    probe,
    myEmail: user?.email ?? null,
    mySub: user?.id ?? null,
  });

  const launchEnabled = state.kind === 'connected-online' && Boolean(mineZeroApp);

  // Most-recent download-progress row for the resolved version. The launcher
  // keeps one row per (user, version); pick the latest by updatedAt so a fresh
  // attempt supersedes a stale Completed/Failed row.
  const download = useMemo<LauncherDownload | null>(() => {
    const targetVid = selectedVersion?.versionId;
    if (targetVid === undefined) return null;
    const rows = downloadProgress.filter((r) => r.versionId === targetVid);
    if (rows.length === 0) return null;
    const row = rows.reduce((latest, r) =>
      r.updatedAt.toDate() > latest.updatedAt.toDate() ? r : latest,
    );
    const total = row.totalBytes;
    const percent =
      total > 0n
        ? Math.min(100, Math.round(Number((row.bytesDownloaded * 100n) / total)))
        : null;
    return {
      status: row.status.tag,
      percent,
      bytesDownloaded: row.bytesDownloaded,
      totalBytes: total,
      errorMessage: row.errorMessage ?? null,
      statusMessage: row.statusMessage ?? null,
    };
  }, [downloadProgress, selectedVersion?.versionId]);

  // #193: is the resolved version already here?
  const installState = useMemo(
    () =>
      resolveInstallState({
        installed: installedVersions,
        appId: effectiveAppId,
        versionId: selectedVersion?.versionId,
      }),
    [installedVersions, effectiveAppId, selectedVersion?.versionId],
  );

  // #194: any download for THIS app, whatever version (progress rows carry
  // no app_id, so map the app's versions in).
  const appVersionIds = useMemo(
    () =>
      new Set(
        appVersions.filter((v) => v.appId === effectiveAppId).map((v) => String(v.versionId)),
      ),
    [appVersions, effectiveAppId],
  );
  const appDownload = useMemo(
    () => latestAppDownload(downloadProgress, appVersionIds),
    [downloadProgress, appVersionIds],
  );
  const appDownloadVersionLabel = useMemo(() => {
    if (!appDownload) return undefined;
    const id = String(appDownload.versionId);
    return appVersions.find((v) => String(v.versionId) === id)?.version;
  }, [appDownload, appVersions]);

  return {
    state,
    statusMessage: getStatusMessage(state),
    effectiveAppId,
    exePath: mineZeroApp?.executablePath ?? null,
    versionId: selectedVersion?.versionId,
    versionLabel: selectedVersion?.version,
    launchEnabled,
    download,
    installState,
    appDownload,
    downloadActive: isDownloadActive(appDownload?.status),
    appDownloadVersionLabel,
  };
}
